import { createUuid } from '@notifi/uuid/browser';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Download, FileCheck } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, apiPost, operationsClient } from '@/lib/api/client';
import { isApiError } from '@/lib/api/errors';
import { publicReportEndpoints } from '@/lib/api/endpoints';
import { formatDateTimeInTimeZone, formatTimeZoneLabel } from '@/lib/date-values';
import { CapturedSignature, DocRenderer } from '@/features/reports/public/DocRenderer';
import { sectionsHaveSignatureRequest } from '@/features/reports/doc-editor/schema';
import type { HostedReportView } from '@/lib/api/types/reports';
import { publicReportPdfUrl } from '@/lib/reports/publicReportLinks';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

export const Route = createFileRoute('/(public)/r/$token')({
  component: PublicReportPage,
  validateSearch: (search) => ({
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
});

type ShareErrorCode =
  | 'SHARE_EXPIRED'
  | 'SHARE_REVOKED'
  | 'SHARE_SUPERSEDED'
  | 'SHARE_NOT_FOUND'
  | 'EMAIL_GATE_REQUIRED'
  | 'EMAIL_GATE_MISMATCH'
  | 'PORTAL_LOGIN_REQUIRED'
  | 'UNKNOWN';

function parseShareErrorCode(err: unknown): ShareErrorCode {
  const msg = err instanceof Error ? err.message : String(err);
  const match = (
    [
      'SHARE_EXPIRED',
      'SHARE_REVOKED',
      'SHARE_SUPERSEDED',
      'SHARE_NOT_FOUND',
      'EMAIL_GATE_REQUIRED',
      'EMAIL_GATE_MISMATCH',
      'PORTAL_LOGIN_REQUIRED',
    ] as const
  ).find((c) => msg.includes(c));
  return match ?? 'UNKNOWN';
}

/**
 * Spec §3.4 — the token this link carries was rotated by a republish. The
 * payload names the live one so the customer can hop to the current version.
 */
function parseLatestToken(err: unknown): string | null {
  const data = isApiError(err) ? err.data : null;
  if (typeof data !== 'object' || data === null) return null;
  const latest = (data as { latestToken?: unknown }).latestToken;
  return typeof latest === 'string' ? latest : null;
}

function ShareErrorScreen({
  code,
  email,
  latestToken,
}: {
  code: ShareErrorCode;
  email: string | undefined;
  latestToken: string | null;
}) {
  const headlines: Record<ShareErrorCode, string> = {
    SHARE_EXPIRED: 'This report has expired',
    SHARE_REVOKED: 'This report link was revoked',
    SHARE_SUPERSEDED: 'A newer version of this report is available',
    SHARE_NOT_FOUND: 'Report not found',
    EMAIL_GATE_REQUIRED: 'Email required to view',
    EMAIL_GATE_MISMATCH: "That email doesn't match our records",
    PORTAL_LOGIN_REQUIRED: 'Sign in to your customer portal',
    UNKNOWN: 'Report unavailable',
  };
  const bodies: Record<ShareErrorCode, string> = {
    SHARE_EXPIRED: 'Please contact us — we can re-share this report on a fresh link.',
    SHARE_REVOKED: 'The team revoked access to this report. Reach out and we can re-issue a link.',
    SHARE_SUPERSEDED:
      'This link points at an earlier version. The latest one is ready for you to view.',
    SHARE_NOT_FOUND: 'Check the URL or contact us to re-share the report.',
    EMAIL_GATE_REQUIRED: 'Add your email to the link as `?email=you@example.com`.',
    EMAIL_GATE_MISMATCH:
      "We couldn't match this email to the customer on file. Try the email we have for you.",
    PORTAL_LOGIN_REQUIRED: 'This report requires a customer-portal login. Sign in to view.',
    UNKNOWN: 'This report cannot be viewed at this time.',
  };
  return (
    <PublicCenteredPage className="bg-stone-50 text-center">
      <div className="max-w-md">
        <h1 className="text-xl font-bold mb-2">{headlines[code]}</h1>
        <p className="text-muted-foreground">{bodies[code]}</p>
        {latestToken ? (
          <Button asChild className="mt-4">
            <Link to="/r/$token" params={{ token: latestToken }} search={{ email }}>
              View the latest version
            </Link>
          </Button>
        ) : null}
      </div>
    </PublicCenteredPage>
  );
}

function PublicReportPage() {
  const { token } = Route.useParams();
  const search = Route.useSearch();

  const { data, isLoading, error } = useApiQuery<HostedReportView>({
    queryKey: ['public-report', token, search.email ?? null],
    queryFn: async () =>
      apiGet(operationsClient, publicReportEndpoints.hostedView(token), {
        params: search.email ? { email: search.email } : undefined,
      }),
    showErrorToast: false,
  });

  // Beacon: log access on mount.
  useEffect(() => {
    if (!data) return;
    const sessionId =
      sessionStorage.getItem('rpt-session') ?? `s-${createUuid().slice(0, 12)}`;
    sessionStorage.setItem('rpt-session', sessionId);
    void apiPost(operationsClient, publicReportEndpoints.accessLog(token), {
      email: search.email,
    } as never);
  }, [data, token, search.email]);

  const styleVars = useMemo(() => {
    if (!data) return {} as React.CSSProperties;
    const { primaryColor, secondaryColor, bodyFont, headingFont } = data.branding;
    return {
      ['--rpt-primary' as never]: primaryColor,
      ['--rpt-secondary' as never]: secondaryColor,
      ['--rpt-body-font' as never]: `"${bodyFont}", system-ui, sans-serif`,
      ['--rpt-heading-font' as never]: `"${headingFont}", system-ui, sans-serif`,
    } as React.CSSProperties;
  }, [data]);

  if (isLoading) {
    return (
      <PublicCenteredPage>
        <Loader2 className="h-6 w-6 animate-spin" />
      </PublicCenteredPage>
    );
  }
  if (error) {
    const code = parseShareErrorCode(error);
    return (
      <ShareErrorScreen code={code} email={search.email} latestToken={parseLatestToken(error)} />
    );
  }
  if (!data) return null;

  const reportTimezoneLabel = formatTimeZoneLabel(data.timezone);

  // The sign block renders where the (first) signatureRequest node sits, or
  // at the end of the document when the template requires a signature but no
  // marker was placed.
  const hasSignatureNode = sectionsHaveSignatureRequest(data.sections);
  const showSignature = data.signatureRequired || data.signatures.length > 0;
  const signatureBlock = showSignature ? (
    <SignatureSection
      token={token}
      label={data.signatureLabel}
      signatures={data.signatures}
      signed={data.signed}
      reportStatus={data.report.status}
      timezone={data.timezone}
    />
  ) : null;

  return (
    // The app shell locks html/body/#root to height:100% overflow:hidden (the
    // dashboard scrolls inside inner panels). Public routes render into that
    // fixed root, so this page owns its own vertical scroll — otherwise a
    // report taller than the viewport can't be scrolled on the hosted view.
    <div
      className="h-dvh overflow-y-auto"
      style={{
        ...styleVars,
        fontFamily: 'var(--rpt-body-font)',
        background: '#fafafa',
      }}
    >
      <main className="max-w-3xl mx-auto bg-white shadow-sm my-8 rounded-lg overflow-hidden">
        <header className="px-8 py-6 border-b-2" style={{ borderColor: 'var(--rpt-primary)' }}>
          {data.branding.logoUrl ? (
            <img
              src={data.branding.logoUrl}
              alt={data.branding.tenantName}
              className="mb-3 h-10 w-auto object-contain"
            />
          ) : null}
          <p className="text-xs font-mono text-muted-foreground">{data.report.number}</p>
          <h1
            className="text-2xl font-bold mt-1"
            style={{ color: 'var(--rpt-primary)', fontFamily: 'var(--rpt-heading-font)' }}
          >
            {data.report.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {data.branding.tenantName}
            {data.report.publishedAt && (
              <>
                {' · '}
                {formatDateTimeInTimeZone(data.report.publishedAt, data.timezone ?? undefined, {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                })}
              </>
            )}
          </p>
          {reportTimezoneLabel && (
            <p className="text-xs text-muted-foreground">Times shown in {reportTimezoneLabel}</p>
          )}
        </header>

        {data.report.customerFacingIntro && (
          <section className="px-8 py-4 bg-stone-50 text-sm">
            {data.report.customerFacingIntro}
          </section>
        )}

        <div className="px-8 py-6">
          <DocRenderer
            sections={data.sections}
            photos={data.photos}
            signatureBlock={hasSignatureNode ? signatureBlock : undefined}
          />
          {!hasSignatureNode && signatureBlock ? (
            <div className="mt-8">{signatureBlock}</div>
          ) : null}
        </div>

        {data.report.customerFacingOutro && (
          <section className="px-8 py-4 border-t text-sm">
            {data.report.customerFacingOutro}
          </section>
        )}

        <footer className="px-8 py-4 border-t bg-stone-50 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <a
              href={publicReportPdfUrl(token, search.email)}
              className="inline-flex items-center text-sm font-medium hover:underline"
              style={{ color: 'var(--rpt-primary)' }}
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </a>
            {data.report.spawnedEstimateId && (
              <Link
                to="/combined-doc/$token"
                params={{ token }}
                search={{ email: search.email }}
                className="inline-flex items-center"
              >
                <Button>
                  <FileCheck className="h-4 w-4 mr-2" />
                  Review &amp; approve estimate
                </Button>
              </Link>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Have a question about this report?{' '}
            <a
              href={`mailto:?subject=Question about report ${data.report.number}`}
              className="underline hover:text-foreground"
            >
              Email us
            </a>
            .
          </p>
        </footer>
      </main>
    </div>
  );
}

function SignatureSection({
  token,
  label,
  signatures,
  signed,
  reportStatus,
  timezone,
}: {
  token: string;
  label: string | null;
  signatures: HostedReportView['signatures'];
  signed: boolean;
  reportStatus: string;
  timezone: string | null;
}) {
  const latest = signatures[0] ?? null;
  return (
    <section
      id="signature"
      className="rounded-lg border-t-4 border bg-white p-5"
      style={{ borderTopColor: 'var(--rpt-primary)' }}
    >
      <h2
        className="text-lg font-semibold mb-3"
        style={{ color: 'var(--rpt-primary)', fontFamily: 'var(--rpt-heading-font)' }}
      >
        Customer Acknowledgment
      </h2>
      {latest ? (
        <CapturedSignature signature={latest} timezone={timezone} />
      ) : signed ? (
        <p className="text-sm font-medium" style={{ color: 'var(--rpt-primary)' }}>
          This report has been signed.
        </p>
      ) : reportStatus === 'PUBLISHED' ? (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            {label ?? 'I acknowledge the findings above.'}
          </p>
          <SignaturePad token={token} />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          A signature will be collected once this report is published.
        </p>
      )}
    </section>
  );
}

function SignaturePad({ token }: { token: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [name, setName] = useState('');
  const [signedName, setSignedName] = useState<string | null>(null);
  const drawingRef = useRef(false);

  const startDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawingRef.current = true;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const rect = c.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };
  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const rect = c.getBoundingClientRect();
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };
  const endDraw = () => {
    drawingRef.current = false;
  };
  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx?.clearRect(0, 0, c.width, c.height);
  };
  const submit = async () => {
    const c = canvasRef.current;
    if (!c || !name.trim()) return;
    const dataUrl = c.toDataURL('image/png');
    await apiPost(operationsClient, publicReportEndpoints.sign(token), {
      signerName: name.trim(),
      imageBase64: dataUrl,
    } as never);
    setSignedName(name.trim());
  };

  if (signedName) {
    return (
      <p className="text-sm font-medium" style={{ color: 'var(--rpt-primary)' }}>
        Thank you, {signedName} — your signature has been recorded.
      </p>
    );
  }

  return (
    <div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name"
        className="block w-full max-w-sm mb-3 px-3 py-2 border rounded-md text-sm"
      />
      <canvas
        ref={canvasRef}
        width={520}
        height={160}
        className="border rounded bg-white touch-none max-w-full"
        onPointerDown={startDraw}
        onPointerMove={draw}
        onPointerUp={endDraw}
        onPointerLeave={endDraw}
      />
      <div className="mt-3 flex gap-2">
        <Button variant="outline" size="sm" onClick={clear} type="button">
          Clear
        </Button>
        <Button size="sm" onClick={submit} disabled={!name.trim()} type="button">
          Submit signature
        </Button>
      </div>
    </div>
  );
}
